import { usePathname, useRouter } from 'expo-router';
import { useSQLiteContext } from 'expo-sqlite';
import {
  CalendarClock,
  Check,
  CheckCircle2,
  ChevronsUpDown,
  FileText,
  Home,
  Package,
  Receipt,
  Scale,
  SkipForward,
  StickyNote,
  Target,
  Users,
} from 'lucide-react-native';
import { type ComponentType, useEffect, useState } from 'react';
import { Pressable, ScrollView, StyleSheet, Text, View, useWindowDimensions } from 'react-native';

import { HomeSwitcher } from '@/components/HomeSwitcher';
import { StaticMapCard } from '@/components/StaticMapCard';
import { WeatherPanel } from '@/components/home/WeatherPanel';
import { listGoals } from '@/lib/goals';
import { Fonts, Palette, Radius, Spacing } from '@/constants/theme';
import { getSubtypeMeta, getSystemMeta } from '@/data/systems';
import type { PropertyRow, SystemRow, GoalRow } from '@/db/types';
import { useDataRevision } from '@/lib/data-revision';
import {
  HVAC_LABEL,
  LOT_LABEL,
  YEAR_LABEL,
} from '@/lib/property-labels';
import {
  type ActiveTask,
  dueLabel,
  getActiveProperty,
  getActiveTasks,
  getCompletedTasks,
  getSystemsForProperty,
} from '@/lib/tasks';
import { parseWidgetKeys } from '@/lib/sidebar-widgets';
import { loadTimeline, type TimelineEntry, type TimelineKind } from '@/lib/timeline';

/**
 * Desktop right sidebar — renders widgets toggled in Settings → Sidebar
 * widgets. Widget order is fixed (catalog order); visibility is per-user
 * via `property.sidebar_widgets_json`.
 *
 * Pulls all data inside the rail (independent fetch) and refreshes on
 * focus so a task completion in the main column flows to the past-tasks
 * widget without a manual refresh.
 */
export function RightRail() {
  // Viewport-anchored height (Wave 68): the fixed wrapper's CSS box
  // mis-computes under RNW when the window shrinks, so the rail caps
  // itself with a live number instead — top offset 72 + bottom 96 + a
  // little breathing room. Recomputes on every window resize.
  const { height: winH } = useWindowDimensions();
  const railMaxH = Math.max(240, winH - 176);
  const db = useSQLiteContext();
  const [property, setProperty] = useState<PropertyRow | null>(null);
  const [past, setPast] = useState<ActiveTask[]>([]);
  const [upcoming, setUpcoming] = useState<ActiveTask[]>([]);
  const [systems, setSystems] = useState<SystemRow[]>([]);
  // Recent activity feed — mixed timeline (tasks, notes, expenses,
  // systems added, etc.) capped at the few latest so the sidebar
  // widget stays glanceable. Same query the My Home Activity card uses.
  const [activity, setActivity] = useState<TimelineEntry[]>([]);
  // Active projects for the Projects widget (Wave 69).
  const [goals, setGoals] = useState<GoalRow[]>([]);

  // Refetch on (a) navigation — pathname dep covers route changes that
  // mutate data implicitly, and (b) explicit DB writes — useDataRevision
  // bumps whenever completeTask/skipTask/etc fires, so the rail stays in
  // sync even when the user is doing things on the same page.
  const pathname = usePathname();
  const revision = useDataRevision();
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const prop = await getActiveProperty(db);
      if (!prop) return;
      const [recent, sys, active, timeline, allGoals] = await Promise.all([
        getCompletedTasks(db, prop.id, 5),
        getSystemsForProperty(db, prop.id),
        getActiveTasks(db, prop.id, 20),
        loadTimeline(db, prop.id, 8),
        listGoals(db, prop.id),
      ]);
      if (cancelled) return;
      setProperty(prop);
      setPast(recent);
      setSystems(sys);
      setUpcoming(active);
      setActivity(timeline);
      // Every non-done project, momentum first (Wave 72 fix: the old
      // in-progress/planning filter showed NOTHING for users whose
      // projects were all still "Someday" dreams — which is everyone
      // right after onboarding).
      const rank: Record<string, number> = { 'in-progress': 0, planning: 1, someday: 2 };
      setGoals(
        allGoals
          .filter((g) => g.status !== 'done')
          .sort((a, b) => (rank[a.status] ?? 3) - (rank[b.status] ?? 3))
          .slice(0, 4)
      );
    })();
    return () => { cancelled = true; };
  }, [db, pathname, revision]);

  const keys = parseWidgetKeys(property?.sidebar_widgets_json);
  // No property yet (fresh install) or user has toggled every widget off
  // — render nothing so the floating wrapper around us collapses
  // visually instead of leaving an empty sand block hanging on screen.
  if (!property || keys.length === 0) {
    return null;
  }

  return (
    <ScrollView
      // flexGrow 0 keeps the wrapper's fixed width authoritative, but
      // maxHeight '100%' is the load-bearing part (Wave 68): the old
      // flex:0/flexShrink:0 combo let the ScrollView size itself to its
      // CONTENT, silently overflowing the wrapper — widgets below the
      // fold (House details) were unreachable because nothing actually
      // scrolled. Capped to the wrapper, overflow scrolls with the
      // globally-styled thin scrollbar.
      style={[styles.rail, { flexGrow: 0, flexShrink: 1, maxHeight: railMaxH }]}
      contentContainerStyle={styles.railInner}
      showsVerticalScrollIndicator={false}>
      {keys.map((k) => {
        switch (k) {
          case 'nickname':
            return <NicknameWidget key={k} property={property} />;
          case 'map':
            return <MapWidget key={k} property={property} />;
          case 'upcoming_tasks':
            return <UpcomingTasksWidget key={k} upcoming={upcoming} />;
          case 'past_tasks':
            return <PastTasksWidget key={k} past={past} />;
          case 'systems':
            return <SystemsWidget key={k} systems={systems} />;
          case 'house_details':
            return <HouseDetailsWidget key={k} property={property} />;
          case 'recent_activity':
            return <RecentActivityWidget key={k} entries={activity} />;
          case 'weather':
            // Compact rail layout (Wave 71) — the full panel's grid
            // turned to soup at 280px.
            return (
              <WeatherPanel
                key={k}
                compact
                zip={property.zip ?? null}
                lat={property.lat ?? null}
                lon={property.lon ?? null}
              />
            );
          case 'projects':
            return <ProjectsWidget key={k} goals={goals} />;
          default:
            return null;
        }
      })}
    </ScrollView>
  );
}

// --- Widgets --------------------------------------------------------------

function NicknameWidget({ property }: { property: PropertyRow }) {
  const nickname = property.name?.trim() || 'Your home';
  const address = property.address?.trim() || property.zip || 'No address set';
  const [switcherOpen, setSwitcherOpen] = useState(false);
  // Tap opens the HomeSwitcher — the primary multi-home affordance on
  // desktop. The chevron-up-down icon hints at "this is a picker," not
  // a static label. v37 routed this tile into the Tune walkthrough;
  // that path moved to the chevron-right arrow on the row itself if
  // we want it back later — the switcher is the higher-leverage action.
  return (
    <>
      <Pressable
        onPress={() => setSwitcherOpen(true)}
        accessibilityLabel="Switch home"
        style={({ pressed }) => [
          styles.card,
          styles.nicknameCard,
          pressed && { opacity: 0.9 },
        ]}>
        <View style={styles.nicknameIcon}>
          <Home size={18} color={Palette.cream} strokeWidth={1.8} />
        </View>
        <View style={{ flex: 1, minWidth: 0 }}>
          <Text style={styles.nicknameName} numberOfLines={1}>{nickname}</Text>
          <Text style={styles.nicknameAddr} numberOfLines={1}>{address}</Text>
        </View>
        <ChevronsUpDown
          size={16}
          color="rgba(251,246,234,0.55)"
          strokeWidth={2}
        />
      </Pressable>
      <HomeSwitcher
        visible={switcherOpen}
        onClose={() => setSwitcherOpen(false)}
      />
    </>
  );
}

function MapWidget({ property }: { property: PropertyRow }) {
  // StaticMapCard already brings its own card chrome + tap-to-edit fallback.
  return (
    <View style={styles.mapWrap}>
      <StaticMapCard
        lat={property.lat ?? null}
        lon={property.lon ?? null}
        address={property.address ?? null}
        height={150}
      />
    </View>
  );
}

function PastTasksWidget({ past }: { past: ActiveTask[] }) {
  const router = useRouter();
  return (
    <View style={styles.card}>
      <View style={styles.cardHead}>
        <Text style={styles.cardLabel}>PAST TASKS</Text>
        {past.length > 0 ? (
          <Pressable
            onPress={() =>
              router.push({ pathname: '/(tabs)/schedule', params: { completed: '1' } } as never)
            }
            hitSlop={6}>
            <Text style={styles.viewAll}>View all</Text>
          </Pressable>
        ) : null}
      </View>
      {past.length === 0 ? (
        <Text style={styles.empty}>
          Mark a task done or skip and it lands here.
        </Text>
      ) : (
        past.map((t, i) => {
          const skipped = t.completion_kind === 'skip';
          return (
            <Pressable
              key={t.id}
              onPress={() => router.push(`/task/${t.id}` as never)}
              style={({ pressed }) => [
                styles.row,
                i < past.length - 1 && styles.rowBorder,
                pressed && { opacity: 0.7 },
              ]}>
              <View style={[styles.rowBadge, skipped && styles.rowBadgeSkip]}>
                {skipped
                  ? <SkipForward size={11} color={Palette.cream} strokeWidth={2.4} />
                  : <CheckCircle2 size={12} color={Palette.cream} strokeWidth={2.2} />}
              </View>
              <View style={{ flex: 1, minWidth: 0 }}>
                <Text style={styles.rowTitle} numberOfLines={1}>{t.title}</Text>
                <Text style={styles.rowMeta} numberOfLines={1}>
                  {relativeTime(t.completed_at ?? 0)}
                  {skipped ? ' · skipped' : ''}
                </Text>
              </View>
            </Pressable>
          );
        })
      )}
    </View>
  );
}

function UpcomingTasksWidget({ upcoming }: { upcoming: ActiveTask[] }) {
  const router = useRouter();
  // Surface the next 5 actionable items so the user sees what's coming
  // without leaving whatever screen they're on. Tap a row to jump into
  // the task detail (where Mark Done / Skip / Reschedule live).
  const visible = upcoming.slice(0, 5);
  return (
    <View style={styles.card}>
      <View style={styles.cardHead}>
        <Text style={styles.cardLabel}>UPCOMING</Text>
        {upcoming.length > 0 ? (
          <Pressable onPress={() => router.push('/(tabs)/schedule' as never)} hitSlop={6}>
            <Text style={styles.viewAll}>View all</Text>
          </Pressable>
        ) : null}
      </View>
      {visible.length === 0 ? (
        <Text style={styles.empty}>
          You&apos;re caught up — nothing on the horizon.
        </Text>
      ) : (
        visible.map((t, i) => (
          <Pressable
            key={t.id}
            onPress={() => router.push(`/task/${t.id}` as never)}
            style={({ pressed }) => [
              styles.row,
              i < visible.length - 1 && styles.rowBorder,
              pressed && { opacity: 0.7 },
            ]}>
            <View style={[styles.rowBadge, styles.rowBadgeUpcoming]}>
              <CalendarClock size={12} color={Palette.cream} strokeWidth={2.2} />
            </View>
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text style={styles.rowTitleActive} numberOfLines={1}>{t.title}</Text>
              <Text style={styles.rowMeta} numberOfLines={1}>
                {dueLabel(t.due_at)}
              </Text>
            </View>
          </Pressable>
        ))
      )}
    </View>
  );
}

function SystemsWidget({ systems }: { systems: SystemRow[] }) {
  const router = useRouter();
  const active = systems.filter((s) => s.is_active === 1);
  return (
    <View style={styles.card}>
      <View style={styles.cardHead}>
        <Text style={styles.cardLabel}>SYSTEMS · {active.length}</Text>
        <Pressable onPress={() => router.push('/(tabs)/my-home' as never)} hitSlop={6}>
          <Text style={styles.viewAll}>View all</Text>
        </Pressable>
      </View>
      {active.length === 0 ? (
        <Text style={styles.empty}>
          Add systems in My Home to see them here.
        </Text>
      ) : (
        active.slice(0, 6).map((s, i) => {
          // Use the systems data registry to resolve the human label —
          // raw codes like 'dis' / 'wh' / 'hvac' are dev artifacts that
          // shouldn't reach the user. `getSubtypeMeta` adds a finer-grain
          // label (e.g. "Furnace") when a subtype is set.
          const meta = getSystemMeta(s.kind);
          const subtype = getSubtypeMeta(s.kind, s.subtype);
          const display = s.nickname?.trim()
            || subtype?.label
            || meta?.label
            || s.kind;
          return (
            <Pressable
              key={s.id}
              onPress={() => router.push(`/system/${s.id}` as never)}
              style={({ pressed }) => [
                styles.row,
                i < Math.min(active.length, 6) - 1 && styles.rowBorder,
                pressed && { opacity: 0.7 },
              ]}>
              <View style={{ flex: 1, minWidth: 0 }}>
                <Text style={styles.rowTitle} numberOfLines={1}>{display}</Text>
                {s.model ? (
                  <Text style={styles.rowMeta} numberOfLines={1}>{s.model}</Text>
                ) : null}
              </View>
            </Pressable>
          );
        })
      )}
    </View>
  );
}

function HouseDetailsWidget({ property }: { property: PropertyRow }) {
  const rows: { label: string; value: string }[] = [];
  if (property.bedrooms != null) rows.push({ label: 'Bedrooms', value: String(property.bedrooms) });
  if (property.bathrooms != null) rows.push({ label: 'Bathrooms', value: String(property.bathrooms) });
  if (property.sqft) rows.push({ label: 'Size', value: `${property.sqft.toLocaleString()} sq ft` });
  if (property.hvac_type) {
    rows.push({ label: 'HVAC', value: HVAC_LABEL[property.hvac_type] ?? property.hvac_type });
  }
  if (property.year_band) {
    rows.push({ label: 'Build', value: YEAR_LABEL[property.year_band] ?? property.year_band });
  }
  if (property.lot_band) {
    rows.push({ label: 'Lot', value: LOT_LABEL[property.lot_band] ?? property.lot_band });
  }
  return (
    <View style={styles.card}>
      <Text style={styles.cardLabel}>HOUSE DETAILS</Text>
      {rows.length === 0 ? (
        <Text style={styles.empty}>
          Edit your home to fill in details. They&apos;ll show up here.
        </Text>
      ) : (
        <View style={{ marginTop: 4 }}>
          {rows.map((r, i) => (
            <View key={r.label} style={[styles.detailRow, i < rows.length - 1 && styles.rowBorder]}>
              <Text style={styles.detailLabel}>{r.label}</Text>
              <Text style={styles.detailValue}>{r.value}</Text>
            </View>
          ))}
        </View>
      )}
    </View>
  );
}

/**
 * Recent activity widget — mixed timeline preview (tasks, notes,
 * expenses, systems, etc.) sourced from `loadTimeline`. Each row is a
 * kind-tinted dot + title + relative time. Tap routes to the kind's
 * detail screen — same routing the My Home Activity card uses.
 */
const ACTIVITY_TINT: Record<
  TimelineKind,
  {
    tint: string;
    bg: string;
    tag: string;
    Icon: ComponentType<{ size?: number; color?: string; strokeWidth?: number }>;
  }
> = {
  completion: { tint: Palette.forest, bg: '#DCE7E1', tag: 'Done', Icon: Check },
  skip: { tint: Palette.clay, bg: 'rgba(176,95,60,0.13)', tag: 'Skipped', Icon: SkipForward },
  expense: { tint: Palette.honeyDeep, bg: 'rgba(225,163,61,0.18)', tag: 'Expense', Icon: Receipt },
  system: { tint: Palette.forest, bg: '#EAF0EA', tag: 'System', Icon: Home },
  inventory: { tint: Palette.honeyDeep, bg: 'rgba(225,163,61,0.14)', tag: 'Inventory', Icon: Package },
  document: { tint: Palette.clay, bg: 'rgba(176,95,60,0.13)', tag: 'Doc', Icon: FileText },
  contact: { tint: Palette.forest, bg: '#EAF0EA', tag: 'Contact', Icon: Users },
  note: { tint: Palette.honeyDeep, bg: Palette.honeySoft, tag: 'Note', Icon: StickyNote },
  quote: { tint: Palette.forestGlow, bg: '#DCE7E1', tag: 'Quote', Icon: Scale },
};

function RecentActivityWidget({ entries }: { entries: TimelineEntry[] }) {
  const router = useRouter();
  return (
    <View style={styles.card}>
      <View style={styles.cardHead}>
        <Text style={styles.cardLabel}>RECENT ACTIVITY</Text>
        {entries.length > 0 ? (
          <Pressable onPress={() => router.push('/timeline' as never)} hitSlop={6}>
            <Text style={styles.viewAll}>Full timeline</Text>
          </Pressable>
        ) : null}
      </View>
      {entries.length === 0 ? (
        <Text style={styles.empty}>
          Nothing logged yet — complete a task or jot a note and it lands here.
        </Text>
      ) : (
        entries.map((e, i) => {
          const meta = ACTIVITY_TINT[e.kind];
          return (
            <Pressable
              key={e.id}
              onPress={() => navigateForActivityEntry(router, e)}
              style={({ pressed }) => [
                styles.row,
                i < entries.length - 1 && styles.rowBorder,
                pressed && { opacity: 0.7 },
              ]}>
              {/* Wave 68: was a solid tint block — read as a broken
                  icon at the bigger sidebar type. Pale chip + glyph. */}
              <View style={[styles.rowBadge, { backgroundColor: meta.bg }]}>
                <meta.Icon size={13} color={meta.tint} strokeWidth={2} />
              </View>
              <View style={{ flex: 1, minWidth: 0 }}>
                <Text style={styles.rowTitle} numberOfLines={1}>{e.title || 'Untitled'}</Text>
                <Text style={styles.rowMeta} numberOfLines={1}>
                  {meta.tag} · {relativeTime(e.at)}
                </Text>
              </View>
            </Pressable>
          );
        })
      )}
    </View>
  );
}

/** Projects widget (Wave 69) — the rail's take on the home tab's
 *  "What you're building toward" peek: active + planning projects,
 *  tap-through to the project viewer. */
function ProjectsWidget({ goals }: { goals: GoalRow[] }) {
  const router = useRouter();
  return (
    <View style={styles.card}>
      <View style={styles.cardHead}>
        <Text style={styles.cardLabel}>PROJECTS</Text>
        <Pressable onPress={() => router.push('/projects' as never)} hitSlop={6}>
          <Text style={styles.viewAll}>View all</Text>
        </Pressable>
      </View>
      {goals.length === 0 ? (
        <Text style={styles.empty}>
          Nothing in flight — add a dream or project and it lands here.
        </Text>
      ) : (
        goals.map((g, i) => (
          <Pressable
            key={g.id}
            onPress={() => router.push(`/projects/${g.id}` as never)}
            style={({ pressed }) => [
              styles.row,
              i < goals.length - 1 && styles.rowBorder,
              pressed && { opacity: 0.7 },
            ]}>
            <View style={[styles.rowBadge, { backgroundColor: 'rgba(225,163,61,0.18)' }]}>
              <Target size={13} color={Palette.honeyDeep} strokeWidth={2} />
            </View>
            <View style={{ flex: 1, minWidth: 0 }}>
              <Text style={styles.rowTitle} numberOfLines={1}>{g.title}</Text>
              <Text style={styles.rowMeta} numberOfLines={1}>
                {g.status === 'in-progress'
                  ? 'In progress'
                  : g.status === 'planning'
                  ? 'Planning'
                  : 'Someday'}
                {g.est_cost_cents
                  ? ` · $${(g.est_cost_cents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`
                  : ''}
              </Text>
            </View>
          </Pressable>
        ))
      )}
    </View>
  );
}

function navigateForActivityEntry(router: ReturnType<typeof useRouter>, e: TimelineEntry): void {
  switch (e.kind) {
    case 'completion':
    case 'skip':
      router.push(`/task/${e.ref_id}` as never);
      break;
    case 'expense':
      router.push(`/expenses/${e.ref_id}` as never);
      break;
    case 'system':
      router.push(`/system/${e.ref_id}` as never);
      break;
    case 'inventory':
      router.push(`/inventory/${e.ref_id}` as never);
      break;
    case 'contact':
      router.push(`/contact/${e.ref_id}` as never);
      break;
    case 'document':
      router.push('/media' as never);
      break;
    case 'note':
      router.push({ pathname: '/notes', params: { focus: e.ref_id } } as never);
      break;
    case 'quote':
      router.push(`/quotes/${e.ref_id}` as never);
      break;
  }
}

// --- helpers --------------------------------------------------------------

function relativeTime(t: number): string {
  if (!t) return '';
  const diff = Date.now() - t;
  const m = Math.floor(diff / 60000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const d = Math.floor(h / 24);
  if (d < 7) return `${d}d ago`;
  return new Date(t).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

// --- styles ---------------------------------------------------------------

const styles = StyleSheet.create({
  rail: {
    width: '100%',
    height: '100%',
    backgroundColor: Palette.sand,
  },
  railInner: {
    paddingTop: Spacing.four,
    paddingBottom: Spacing.six,
    gap: Spacing.three,
    // The map widget bleeds its margins slightly negative on the left;
    // give the scroll-content a hair of horizontal padding to compensate
    // and prevent that bleed from spilling outside the sidebar wrapper.
    paddingHorizontal: 4,
  },
  card: {
    backgroundColor: Palette.card,
    borderWidth: 1,
    borderColor: Palette.line,
    borderRadius: Radius.xl,
    padding: Spacing.three,
  },
  cardHead: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  cardLabel: {
    fontFamily: Fonts.sansBold,
    fontSize: 12.5,
    color: Palette.inkFaint,
    letterSpacing: 1.1,
  },
  viewAll: {
    fontFamily: Fonts.sansBold,
    fontSize: 13.5,
    color: Palette.forest,
  },
  empty: {
    fontFamily: Fonts.sansMedium,
    fontSize: 13,
    color: Palette.inkSoft,
    lineHeight: 17,
    paddingVertical: 4,
  },
  // Nickname widget — forest-tinted hero card with house icon.
  nicknameCard: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 11,
    backgroundColor: Palette.forest,
    borderColor: 'rgba(255,255,255,0.06)',
  },
  nicknameIcon: {
    width: 36, height: 36, borderRadius: 11,
    backgroundColor: 'rgba(255,255,255,0.12)',
    alignItems: 'center', justifyContent: 'center',
  },
  nicknameName: {
    fontFamily: Fonts.serifBold,
    fontSize: 15,
    color: Palette.cream,
    letterSpacing: -0.2,
  },
  nicknameAddr: {
    marginTop: 2,
    fontFamily: Fonts.sansMedium,
    fontSize: 13.5,
    color: 'rgba(251,246,234,0.7)',
  },
  // Map widget wrapper — strip the StaticMapCard's default outer margin
  // by overriding to zero so it lays out flush inside the rail at the
  // sidebar's width instead of pulling outside the wrapper bounds.
  weatherWrap: {
    marginHorizontal: -Spacing.four,
    marginTop: -Spacing.three,
  },
  mapWrap: {
    marginHorizontal: -Spacing.four,
    paddingHorizontal: 0,
    overflow: 'hidden',
  },
  row: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 9,
    paddingVertical: 8,
  },
  rowBorder: {
    borderBottomWidth: 1,
    borderBottomColor: Palette.line,
  },
  rowBadge: {
    width: 24, height: 24, borderRadius: 8,
    backgroundColor: Palette.moss,
    alignItems: 'center', justifyContent: 'center',
  },
  rowBadgeSkip: {
    backgroundColor: Palette.clay,
  },
  rowBadgeUpcoming: {
    backgroundColor: Palette.honey,
  },
  rowTitleActive: {
    fontFamily: Fonts.sansBold,
    fontSize: 13.5,
    color: Palette.ink,
  },
  rowTitle: {
    fontFamily: Fonts.sansBold,
    fontSize: 13.5,
    color: Palette.ink,
  },
  rowMeta: {
    marginTop: 1,
    fontFamily: Fonts.sansMedium,
    fontSize: 13,
    color: Palette.inkSoft,
  },
  detailRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: 7,
  },
  detailLabel: {
    fontFamily: Fonts.sansMedium,
    fontSize: 13.5,
    color: Palette.inkSoft,
  },
  detailValue: {
    fontFamily: Fonts.sansBold,
    fontSize: 13,
    color: Palette.ink,
  },
});
